import React, { useCallback, useEffect } from 'react';
import * as Routes from 'modules/routes';
import { Input } from '@/components/ui/Form';
import cn from '@/utils/cn';
import { useForm } from 'react-hook-form';
import { useQueryClient, useMutation } from '@tanstack/react-query';
import { IQuestion, IQuestionAnswer } from '@/components/staffer/Grading/types';
import { useDebounceCallback } from 'usehooks-ts';
import Axios from 'axios';
import useGradingStore from '@/components/staffer/Grading/useGradingStore';
import Tooltip from '@/components/common/Tooltip';
import { formatLocalTimestamp } from '@/components/common/Utils';
import I18n from 'i18n-js';
import checkIcon from '../../../../images/icons/check-thin.svg';
import timesIcon from '../../../../images/icons/times.svg';

const ScoreInput = ({ attrName, disabled, hasErrors, formRegister, isTeacher }) => (
  <Tooltip
    content="This question is automatically graded"
    shouldDisplay={isTeacher && disabled}
  >
    <Input
      className="tw-text-center tw-p-0.5 tw-h-8 disabled:tw-bg-light-grey"
      containerClasses={cn('tw-mx-1 tw-w-8 tw-h-8 tw-p-0', {
        'tw-border-red tw-border-[1px] tw-border-solid tw-rounded': hasErrors,
      })}
      disabled={disabled}
      name={attrName}
      step="0.1"
      type="number"
      {...formRegister}
    />
  </Tooltip>
);

interface ScoreEditProps {
  question: IQuestion;
  questionAnswer?: IQuestionAnswer;
  studentId: number | string;
}

const ScoreEdit = ({ question, questionAnswer, studentId }: ScoreEditProps) => {
  const { assignment, classroom, isTeacher, submission, updateQuestionAnswer } = useGradingStore();

  const client = useQueryClient();

  const getFormattedScore = val => (
    (!Number.isNaN(val) && val % 1 === 0) ? Math.floor(val) : val
  );

  const score = parseFloat(questionAnswer?.score);
  const formattedScore = getFormattedScore(score);

  const {
    formState: { errors },
    getFieldState,
    handleSubmit,
    register,
    setValue,
    trigger,
  } = useForm({
    defaultValues: { scoreInput: formattedScore },
  });

  // Update form value when questionAnswer prop changes
  useEffect(() => {
    const newScore = parseFloat(questionAnswer?.score);
    const newFormattedScore = getFormattedScore(newScore);
    setValue('scoreInput', newFormattedScore);
  }, [question.id, questionAnswer?.score, setValue]);

  const saveMutation = useMutation({
    mutationFn: (data: { scoreInput: string | number }) => Axios.post(
      Routes.plato_api_grade_question_answer_path(studentId, {
        assignment_id: assignment.id,
        assignment_type: assignment.assignment_details.type,
        question_answer_id: questionAnswer?.id,
        question_id: question.id,
        score: data.scoreInput,
      }),
    ),
    mutationKey: ['gradeQuestionAnswer', studentId, question.id],
    onError: (error) => {
      // eslint-disable-next-line no-console
      console.error('Error updating score:', error);
    },
    onSuccess: async (res) => {
      await client.invalidateQueries(['submission', submission?.id.toString()]);
      await client.invalidateQueries(['studentAnswer', submission?.user_id]);
      await client.invalidateQueries(['studentAnswer', classroom?.id]);

      const { data } = res;
      if (data.success) updateQuestionAnswer(data);
    },
  });

  // Reset mutation state when question or student changes
  useEffect(() => {
    saveMutation.reset();
  }, [question.id, studentId]);

  const handleSave = (data: { scoreInput: string | number }) => {
    saveMutation.mutate(data);
  };

  const debouncedSave = useCallback(useDebounceCallback(handleSave, 1000), [question.id]);

  const handleInputChange = async (attrName) => {
    const isValid = await trigger(attrName);
    if (isValid) handleSubmit(debouncedSave)();
  };

  const formRegister = attrName => (
    register(attrName, {
      max: { message: 'Value cannot exceed max points', value: question.points },
      min: { message: 'Value cannot be less than 0', value: 0 },
      onChange: () => handleInputChange(attrName),
      required: true,
    })
  );

  const giveCreditButton = (fullCredit: boolean) => {
    const message = fullCredit ? 'Give Full Credit' : 'Give No Credit';
    return (
      <Tooltip content={message} size="medium" theme="white">
        <button
          className="tw-ml-1 tw-px-2 tw-py-2 tw-rounded tw-border tw-border-solid tw-border-[#ccc] tw-bg-transparent
           tw-cursor-pointer tw-w-9 tw-h-8 tw-flex tw-justify-center tw-items-center"
          type="button"
          onClick={() => {
            setValue('scoreInput', fullCredit ? question.points : 0, {
              shouldDirty: true,
              shouldTouch: true,
              shouldValidate: true,
            });
            handleInputChange('scoreInput');
          }}
        >
          <img alt={message} className="tw-w-8 tw-h-8" src={fullCredit ? checkIcon : timesIcon} />
        </button>
      </Tooltip>
    );
  };

  const statusMessage = ({ isError = false, message }: { isError?: boolean, message: string }) => (
    <p className={`tw-inline-block tw-text-sm ${isError ? 'tw-text-red' : 'tw-text-green'}`}>
      {message}
    </p>
  );

  const renderSaveStatus = () => {
    const fieldError = getFieldState('scoreInput').error;

    if (fieldError) return statusMessage({ isError: true, message: fieldError.message });
    if (saveMutation.isLoading) return statusMessage({ message: `${I18n.t('saving')}…` });
    if (saveMutation.isError) return statusMessage({ isError: true, message: 'Save failed' });
    if (saveMutation.isSuccess) {
      const savedTime = formatLocalTimestamp(new Date(), true);
      return statusMessage({ message: `${I18n.t('saved_at')} ${savedTime}` });
    }

    return null;
  };

  return (
    <div className="tw-w-full tw-py-4">
      <div className="tw-flex tw-justify-end tw-items-center">
        <strong>Score:</strong>
        {(isTeacher && !question.auto_gradable) && (
          <div className="tw-mr-1 tw-flex">
            {giveCreditButton(true)}
            {giveCreditButton(false)}
          </div>
        )}
        <ScoreInput
          attrName="scoreInput"
          disabled={!isTeacher || question.auto_gradable}
          formRegister={formRegister('scoreInput')}
          hasErrors={Object.keys(errors).includes('scoreInput') || getFieldState('scoreInput').error}
          isTeacher={isTeacher}
        />
        /
        <div>{question.points}</div>
      </div>
      <div className="tw-flex tw-justify-end tw-mt-1">
        {renderSaveStatus()}
      </div>
    </div>
  );
};

export default ScoreEdit;
